import { useMemo } from 'react';
import { useAuthStore } from '@shared/stores/authStore';
import { routeForModule } from '@shared/lib/moduleRoutes';
import { permissionsAllowAny } from '@shared/lib/permissions';
import type { CompiledMoreTileCategory, CompiledPublicTile } from '@shared/types/domainConfig';

/**
 * Permission gating for compiled domain-config tiles.
 *
 * The backend compiles `tiles.main` per tenant by *module*, not by the caller's
 * role — so a member whose role lacks a module's permission still sees its tile
 * and only finds out on the 403 inside. This mirrors the tenant-api's
 * `requirePermission` on the endpoint each module's landing screen calls first,
 * so those tiles never render in the first place.
 *
 * Keyed by the in-app route a tile opens (the tile's `module_route`, else its
 * `module_required` mapped through `routeForModule`) — the one key space both
 * tile shapes resolve to. ANY listed permission grants entry, matching
 * `requirePermission`'s own OR semantics.
 *
 * A route that is absent here is NOT permission-gated: its endpoints are
 * module-gated only (facilities, events, support, documents, gallery, news,
 * suppliers, utilities, properties, billing, products, orders, map,
 * communication all authenticate but require no permission). Unmapped means
 * visible — we never hide a tile on a rule we do not have.
 */
const ROUTE_PERMISSIONS: Record<string, string[]> = {
  // GET /classifieds
  '/app/classifieds': ['classifieds:read:all', 'classifieds:read:own'],
  // GET /pet-types/active — the member's own pets list itself is unguarded.
  '/app/pets': [
    'pets:read:all',
    'pets:read:own',
    'pets:create:own',
    'pets:update:own',
    'pets:manage:types',
  ],
  // GET /vehicles, GET /vehicle-types/active
  '/app/vehicles': ['vehicles:read:all', 'vehicles:read:own', 'vehicles:manage:types'],
  // GET /panic/emergency-types/active, POST /panic/trigger
  '/app/panic': ['panic:trigger'],
  // GET /survey-responses/available — also reached by eForm (flow_template) tiles.
  '/app/surveys': ['surveys:response:submit'],
  '/app/eforms': ['surveys:response:submit'],
  // GET /public/me/member-contacts, GET /public/me/access-entry-types
  '/app/access-invites': [
    'access_management:member_contacts:read_own',
    'access_management:invites:create_own',
  ],
};

/** The route a tile opens, normalised to the key `ROUTE_PERMISSIONS` uses. */
function guardRoute(tile: CompiledPublicTile): string | null {
  if (tile.action_type === 'flow_template') return '/app/eforms';
  if (tile.action_type !== 'module') return null;
  return tile.action_config.module_route ?? routeForModule(tile.module_required);
}

/** Can this member open the screen at `route`? Unmapped routes are open. */
export function canAccessRoute(route: string | null | undefined, permissions: string[]): boolean {
  const required = route ? ROUTE_PERMISSIONS[route] : undefined;
  if (!required) return true;

  return permissionsAllowAny(permissions, required);
}

export function canAccessTile(tile: CompiledPublicTile, permissions: string[]): boolean {
  return canAccessRoute(guardRoute(tile), permissions);
}

/** Drop the tiles this member cannot open. */
export function filterAccessibleTiles(
  tiles: CompiledPublicTile[],
  permissions: string[],
): CompiledPublicTile[] {
  return tiles.filter((tile) => canAccessTile(tile, permissions));
}

/** Same for `more_categories`, dropping any group left with no reachable tile. */
export function filterAccessibleCategories(
  categories: CompiledMoreTileCategory[],
  permissions: string[],
): CompiledMoreTileCategory[] {
  return categories
    .map((category) => ({
      ...category,
      tiles: filterAccessibleTiles(category.tiles ?? [], permissions),
    }))
    .filter((category) => category.tiles.length > 0);
}

/**
 * Reactive tile filters bound to the member's permissions — they re-run on
 * login, sign-out and tenant switch.
 */
export function useAccessibleTiles(tiles: CompiledPublicTile[]): CompiledPublicTile[] {
  const permissions = usePermissions();
  return useMemo(() => filterAccessibleTiles(tiles, permissions), [tiles, permissions]);
}

export function useAccessibleCategories(
  categories: CompiledMoreTileCategory[],
): CompiledMoreTileCategory[] {
  const permissions = usePermissions();
  return useMemo(() => filterAccessibleCategories(categories, permissions), [categories, permissions]);
}

/** Predicate for launchers that carry a route rather than a tile (nav drawer items). */
export function useCanAccessRoute(): (route: string | null | undefined) => boolean {
  const permissions = usePermissions();
  return useMemo(() => (route) => canAccessRoute(route, permissions), [permissions]);
}

const NO_PERMISSIONS: string[] = [];

function usePermissions(): string[] {
  return useAuthStore((state) => state.user?.permissions ?? NO_PERMISSIONS);
}
